import React, { useState, useEffect, useRef } from "react";
import TrayScanner from "./TrayScanner";
import Tray from "./Tray";
import SmallTray from "./SmallTray";
import { fetchJson, post } from "../../lib/Fetching";
import { findSamples } from "./FetchTrays";
import LaboratorySampleSearch from "../sample_reception/LaboratorySampleSearch";
import TestingCategoriesSelect from "../result_notifications/TestingCategoriesSelect";
import moment from "moment";
import { useDebouncedEffect } from "../../lib/useDebouncedEffect";
import Link from "../Link";
import Swal from "sweetalert2";

const TESTING_CATEGORY_MICROBIOLOGY = 2;

function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function Queue({ onQueueChange }) {
  this.queue = [];
  this.add = ({ reference }) => {
    this.queue.current.push({
      uuid: uuidv4(),
      reference,
      status: "pending",
      error: null,
    });
  };
}

export default function PopulateTrays({
  refreshTrays,
  receivingBranchId,
  laboratory_company_id,
}) {
  const activeTrayRef = useRef();
  const [trays, setTrays] = useState([]);
  const [activeTrayId, setActiveTrayId] = useState(null);
  const requestQueue = useRef([]);
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);
  const [allowReceivedSample, setAllowReceivedSample] = useState(false);
  const [browseUnreceivedSamples, setBrowseUnreceivedSamples] = useState(true);
  const [testingCategoryIds, setTestingCategoryIds] = useState([TESTING_CATEGORY_MICROBIOLOGY]);
  const [receiveSamplesDetails, setReceiveSamplesDetails] = useState({
    company_id: null,
    generation: 1,
  });

  const receiveFormParams = {
    company_id: receiveSamplesDetails.company_id,
    company_name: "Same as last sample scanned above",
    start_date: moment()
      .subtract(61, "days")
      .format("YYYY-MM-DD"),
  };

  const fetchSamples = () => {
    if (!activeTrayId || !activeTrayRef) return;
    refreshActiveTray();
    activeTrayRef.current.reloadSamples();
  };

  const addTray = (tray) => {
    const existingTray = trays.find((t) => t.id === tray.id);
    if (existingTray) {
      setActiveTrayId(existingTray.id);
      return;
    }
    setTrays(trays.concat(tray));
    setActiveTrayId(tray.id);
    refreshTrays();
  };

  const refreshActiveTray = () => {
    fetchJson("/trays/trays/" + activeTrayId + ".json").then((json) => {
      setTrays(trays.map((t) => (t.id === activeTrayId ? json.tray : t)));
    });
  };

  const addSample = async (scannedCode) => {
    if (!activeTrayId) {
      alert("There is no active tray!");
      return;
    }
    const uuid = uuidv4();
    requestQueue.current.push({
      uuid: uuid,
      reference: scannedCode,
      status: "pending",
      error: null,
    });
    forceUpdate();
    return post("/trays/trays/" + activeTrayId + "/add_sample", {
      scanned_code: scannedCode,
      receiving_branch_id: receivingBranchId,
      allow_received_sample: allowReceivedSample,
      testing_category_ids: testingCategoryIds,
    }).then((json) => {
      if (json.errors) {
        requestQueue.current.map((item) => {
          if (item.uuid === uuid) {
            item.status = "error";
            item.error = json.errors;
          }
          return item;
        });
        forceUpdate();
      } else {
        requestQueue.current.map((item) => {
          if (item.uuid == uuid) {
            item.status = "completed";
          }
          return item;
        });
        if (json.company_id) {
          setReceiveSamplesDetails({
            company_id: json.company_id,
            generation: receiveSamplesDetails.generation + 1,
          });
        }
        if (json.receipt_notification) {
          const swalBoostrap = Swal.mixin({
            customClass: {
              confirmButton: "btn btn-success"
            },
            buttonsStyling: false
          });
          swalBoostrap.fire({
            title: `Sample ${json.sample_id}`,
            html: json.receipt_notification,
            icon: "warning",
            confirmButtonText: "OK",
            didOpen: () => {
              const popup = swalBoostrap.getPopup();
              popup.addEventListener("keydown", function (e) {
                if (e.key === "Enter") {
                  e.preventDefault();     // Stop default "confirm" behavior
                  e.stopPropagation();    // Stop event from bubbling up
                }
              });
            }
          });
        }
        fetchSamples();
      }
    });
  };

  const displayQueue = requestQueue.current.filter((item) =>
    ["pending", "error"].includes(item.status)
  );

  const removeTray = (trayId) => {
    setActiveTrayId(null);
    setTrays(trays.filter((t) => t.id !== trayId));
    refreshTrays();
  };

  const activeTray = trays.filter((t) => t.id === activeTrayId)[0];

  return (
    <div>
      <div className="basic-panel with-padding with-shadow" style={{ marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Receive and Schedule Samples</h3>
        <p style={{ fontSize: "14pt" }}>
          Here you can scan the trays you are using and samples you are receiving. Scanning a
          sample will receive it into the selected tray and print its barcode label.
        </p>
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <div style={{ width: "70%", marginRight: 20 }}>
            <TrayScanner
              onScanTray={(tray) => addTray(tray)}
              onScanSample={addSample}
              placeholder="Scan tray or sample... scanning a sample into a tray will receive it and print the barcode label"
            />
          </div>
          <div style={{ width: "30%" }}>
            <TestingCategoriesSelect
              testing_category_ids={testingCategoryIds}
              handleSelectChange={(options) => {
                setTestingCategoryIds(options.map((o) => o.value));
              }}
            />
          </div>
        </div>
        <p style={{ fontSize: "14pt", marginBottom: 0 }}>
          <label style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              checked={allowReceivedSample}
              onClick={() => setAllowReceivedSample(!allowReceivedSample)}
            />{" "}
            &nbsp; Allow already received sample?
          </label>
        </p>

        {displayQueue.length > 0 && (
          <table className="table table-condensed">
            <thead>
              <tr>
                <th style={{ width: 30 }} />
                <th style={{ width: 140 }}>Sample Scanned</th>
                <th style={{ width: 140 }}>Status</th>
                <th>Errors</th>
              </tr>
            </thead>
            <tbody>
              {displayQueue.map((item) => (
                <tr>
                  <td>
                    {item.status == "pending" && (
                      <span className="fa fa-spinner fa-spin fa-spinner" />
                    )}
                    {item.status == "error" && (
                      <span className="fa fa-spinner fa-exclamation" />
                    )}
                  </td>
                  <td>{item.reference}</td>
                  <td>{item.status}</td>
                  <td>{item.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {trays.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              flexWrap: "wrap",
              marginTop: 20,
            }}
          >
            {trays.map((t, index) => (
              <SmallTray
                key={t.id}
                tray={t}
                small
                active={t.id === activeTrayId}
                handleActivateTray={() => setActiveTrayId(t.id)}
                style={{ marginBottom: 20 }}
                medium
                showCheckbox={false}
              />
            ))}
          </div>
        )}
        {activeTray && (
          <Tray
            key={activeTrayId}
            ref={activeTrayRef}
            tray={activeTray}
            active
            style={{ marginBottom: 0 }}
            removeTray={() => removeTray(activeTrayId)}
          />
        )}
      </div>
      <hr />
      <h3>
        <Link onClick={() => setBrowseUnreceivedSamples(!browseUnreceivedSamples)}>
          {browseUnreceivedSamples ? (
            <i className="fa fa-caret-down" />
          ) : (
            <i className="fa fa-caret-up" />
          )}
          &nbsp; Browse All Unreceived Samples
        </Link>
      </h3>

      {browseUnreceivedSamples && (
        <LaboratorySampleSearch
          key={receiveSamplesDetails.generation}
          laboratory_company_id={laboratory_company_id}
          initialFormParams={receiveFormParams}
          forceSearch={!!receiveSamplesDetails.company_id}
        />
      )}
    </div>
  );
}
